import React, { useState } from "react";
import { Share, Plus, Copy, X, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * InstallModal — visual install instructions, branching on platform.
 *
 * Variants:
 *   ios_safari        → Tap Share, then Add to Home Screen (with iconography)
 *   ios_other_browser → "Open this page in Safari first" + copy-link
 *   ios_in_app        → "Open in Safari to install" (same copy-link, slightly different copy)
 *   desktop_no_prompt → "Open on your phone to install" + copy-link
 */
export default function InstallModal({ variant, onClose }) {
  const [copied, setCopied] = useState(false);

  const url = typeof window !== "undefined" ? window.location.origin : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Long-press the link below to copy.");
    }
  };

  // -------- shared chrome --------
  const Frame = ({ title, children }) => (
    <div
      data-testid="install-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-foreground/40 backdrop-blur-sm p-0 sm:p-6 animate-in fade-in duration-200"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full sm:max-w-md bg-background hairline shadow-2xl p-6 sm:p-8 animate-in slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="install-modal-close"
          onClick={onClose}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        <div className="label mb-3">Install Cor·relate</div>
        <h2 className="serif text-3xl text-foreground leading-tight">{title}</h2>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );

  // -------- iOS Safari: full visual walkthrough --------
  if (variant === "ios_safari") {
    return (
      <Frame title="Three taps to add it to your home screen.">
        <ol className="space-y-5 text-foreground">
          <li className="flex items-start gap-4">
            <span className="mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] mt-1">01</span>
            <p className="serif italic text-base leading-relaxed">
              Tap the{" "}
              <Share
                size={16}
                strokeWidth={1.8}
                className="inline -mt-1 text-[hsl(var(--primary))]"
              />{" "}
              <span className="serif">Share</span> button — bottom centre of Safari.
            </p>
          </li>
          <li className="flex items-start gap-4">
            <span className="mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] mt-1">02</span>
            <p className="serif italic text-base leading-relaxed">
              Scroll the share sheet, then choose{" "}
              <span className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] border border-[hsl(var(--accent))/0.4] px-2 py-0.5 align-baseline">
                <Plus size={11} strokeWidth={2.5} /> Add to Home Screen
              </span>
            </p>
          </li>
          <li className="flex items-start gap-4">
            <span className="mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] mt-1">03</span>
            <p className="serif italic text-base leading-relaxed">
              Tap <span className="serif">Add</span>. The Correlate &ldquo;C&rdquo; will live next to your other apps.
            </p>
          </li>
        </ol>
        <p className="mt-8 text-[12px] text-muted-foreground leading-relaxed">
          iOS doesn&apos;t let websites trigger this for you — Apple reserves it for the Share menu. Once added, the app opens full-screen, works offline, and lives outside your browser.
        </p>
      </Frame>
    );
  }

  // -------- iOS Chrome/Firefox/Edge or in-app browser: open in Safari --------
  if (variant === "ios_other_browser" || variant === "ios_in_app") {
    return (
      <Frame title="Open this in Safari to install.">
        <p className="serif italic text-base text-muted-foreground leading-relaxed">
          {variant === "ios_in_app"
            ? "In-app browsers can't add pages to your home screen. Tap the menu (•••) and choose Open in Safari to continue."
            : "Only Safari can install web apps on iPhone — it's an Apple restriction. Open this link in Safari, then tap the Share button to Add to Home Screen."}
        </p>
        <div className="mt-6 p-4 bg-secondary hairline">
          <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Copy this link</div>
          <div className="serif text-sm text-foreground break-all select-all">{url}</div>
        </div>
        <button
          data-testid="install-copy-link"
          onClick={copy}
          className="btn-gold w-full mt-5 inline-flex items-center justify-center gap-2"
        >
          {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
        </button>
      </Frame>
    );
  }

  // -------- Desktop without beforeinstallprompt --------
  return (
    <Frame title="Open it on your phone to install.">
      <p className="serif italic text-base text-muted-foreground leading-relaxed">
        Correlate is a Progressive Web App — it installs straight to your phone&apos;s home screen, no app store needed. Open this link on your phone (Safari on iPhone, Chrome on Android) and you&apos;ll see an Install / Add to Home Screen prompt.
      </p>
      <div className="mt-6 p-4 bg-secondary hairline">
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Open on your phone</div>
        <div className="serif text-sm text-foreground break-all select-all">{url}</div>
      </div>
      <button
        data-testid="install-copy-link"
        onClick={copy}
        className="btn-gold w-full mt-5 inline-flex items-center justify-center gap-2"
      >
        {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
      </button>
    </Frame>
  );
}
